import { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, AlertCircle, Loader2, Plus, Trash2, Link } from 'lucide-react';
import { submitRegistration } from '@/api/registrationApi';
import type { RegistrationPayload } from '@/api/registrationApi';

const steps = [
  { id: 1, title: 'Выбор направления', description: 'Выбери компетенцию' },
  { id: 2, title: 'Личные данные', description: 'Заполни анкету' },
  { id: 3, title: 'Портфолио', description: 'Загрузи работы (опционально)' },
  { id: 4, title: 'Подтверждение', description: 'Проверь и отправь' },
];

const competences = [
  { id: 'game-dev', label: 'Разработка компьютерных игр', icon: '🎮' },
  { id: '3d-modeling', label: '3D‑моделирование для компьютерных игр', icon: '🖥️' },
  { id: 'game-design', label: 'Геймдизайн', icon: '🎨' },
];

export default function RegistrationPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCompetence, setSelectedCompetence] = useState<string>('');
  const [formData, setFormData] = useState({
    fullName: '',
    birthDate: '',
    email: '',
    phone: '',
    city: '',
    institution: '',
    portfolioLinks: [''] as string[],
    agreeTerms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleLinkChange = (index: number, value: string) => {
    const updated = [...formData.portfolioLinks];
    updated[index] = value;
    setFormData({ ...formData, portfolioLinks: updated });
  };

  const handleAddLink = () => {
    setFormData({ ...formData, portfolioLinks: [...formData.portfolioLinks, ''] });
  };

  const handleRemoveLink = (index: number) => {
    const updated = formData.portfolioLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, portfolioLinks: updated.length ? updated : [''] });
  };

  const nextStep = () => {
    if (currentStep < steps.length) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agreeTerms) {
      setSubmitError('Необходимо согласие на обработку персональных данных');
      return;
    }

    setSubmitError(null);
    setSubmitting(true);

    try {
      const payload: RegistrationPayload = {
        competence: selectedCompetence,
        fullName: formData.fullName,
        birthDate: formData.birthDate,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        institution: formData.institution,
        agreeTerms: formData.agreeTerms,
        portfolioLinks: formData.portfolioLinks.filter(l => l.trim()),
      };

      await submitRegistration(payload);
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Произошла ошибка при отправке');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900">Выбери направление</h3>
            <p className="text-gray-600">
              От выбора направления зависит характер заданий и состав экспертов.
              Ты можешь участвовать только в одной компетенции.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {competences.map((comp) => (
                <button
                  key={comp.id}
                  type="button"
                  onClick={() => setSelectedCompetence(comp.id)}
                  className={`p-6 border-2 rounded-xl text-left transition-all ${
                    selectedCompetence === comp.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-primary-300'
                  }`}
                >
                  <div className="text-3xl mb-4">{comp.icon}</div>
                  <h4 className="text-xl font-bold text-gray-900">{comp.label}</h4>
                  <p className="mt-2 text-gray-600 text-sm">
                    {comp.id === 'game-dev' && 'Программирование на C#, Unity, создание игровых механик.'}
                    {comp.id === '3d-modeling' && 'Моделирование в Blender, текстурирование, анимация.'}
                    {comp.id === 'game-design' && 'Проектирование геймплея, балансировка, нарратив.'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900">Личные данные</h3>
            <p className="text-gray-600">
              Заполни анкету. Все поля обязательны для заполнения, кроме указанных.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ФИО *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Иванов Иван Иванович"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дата рождения *
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="example@mail.ru"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Телефон *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="+7 (999) 123-45-67"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Город *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Ханты-Мансийск"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Учебное заведение *
                </label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Школа №1, Колледж информатики"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900">Портфолио (опционально)</h3>
            <p className="text-gray-600">
              Если у тебя есть готовые работы, приложи ссылки на облако с файлами портфолио.
              Это повысит твои шансы на прохождение в финал.
            </p>
            <div className="space-y-3">
              {formData.portfolioLinks.map((link, index) => (
                <div key={index} className="flex gap-2">
                  <div className="relative flex-1">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => handleLinkChange(index, e.target.value)}
                      placeholder="https://disk.yandex.ru/d/... или https://github.com/..."
                      className="input-field pl-10"
                    />
                  </div>
                  {formData.portfolioLinks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(index)}
                      className="p-2.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label="Удалить ссылку"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddLink}
                className="btn-secondary inline-flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Добавить ссылку
              </button>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-2">Что указать в ссылках?</h4>
              <ul className="list-disc pl-5 text-gray-600 space-y-1">
                <li>Облако с файлами (Яндекс.Диск, Google Drive, Облако Mail)</li>
                <li>Ссылки на itch.io, GitHub, ArtStation</li>
                <li>Дизайн-документы (геймдизайн)</li>
                <li>Видео демонстрации работ</li>
              </ul>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900">Проверь данные</h3>
            <p className="text-gray-600">
              Убедись, что все данные верны. После отправки изменить заявку будет нельзя.
            </p>
            <div className="card">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Направление</h4>
                  <p className="text-gray-700">
                    {competences.find(c => c.id === selectedCompetence)?.label || 'Не выбрано'}
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Контактные данные</h4>
                  <p className="text-gray-700">
                    {formData.fullName}<br />
                    {formData.birthDate}<br />
                    {formData.email}<br />
                    {formData.phone}<br />
                    {formData.city}, {formData.institution}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <h4 className="font-bold text-gray-900 mb-4">Портфолио</h4>
                  {formData.portfolioLinks.filter(l => l.trim()).length > 0 ? (
                    <ul className="list-disc pl-5 text-gray-700 space-y-1">
                      {formData.portfolioLinks.filter(l => l.trim()).map((link, i) => (
                        <li key={i}><a href={link} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">{link}</a></li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 italic">Не указано</p>
                  )}
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleInputChange}
                    className="h-5 w-5 text-primary-600 rounded"
                    required
                  />
                  <label htmlFor="agreeTerms" className="ml-3 text-gray-700">
                    Я согласен на обработку персональных данных и ознакомлен с{' '}
                    <a href="/privacy" className="text-primary-600 hover:underline">
                      политикой конфиденциальности
                    </a>
                  </label>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center">
                <Check className="h-6 w-6 text-green-600 mr-3" />
                <div>
                  <h4 className="font-bold text-green-800">Готово к отправке!</h4>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {submitSuccess ? (
        <div className="text-center py-16">
          <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Заявка отправлена!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Спасибо за регистрацию на ОЧКИ 2026. Мы свяжемся с тобой в ближайшее время.
          </p>
          <button
            type="button"
            onClick={() => {
              setSubmitSuccess(false);
              setCurrentStep(1);
              setSelectedCompetence('');
              setFormData({
                fullName: '',
                birthDate: '',
                email: '',
                phone: '',
                city: '',
                institution: '',
                portfolioLinks: [''],
                agreeTerms: false,
              });
            }}
            className="btn-primary inline-flex items-center"
          >
            Вернуться на главную
          </button>
        </div>
      ) : (
      <>
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Регистрация на чемпионат</h1>
        <p className="mt-4 text-xl text-gray-600">
          Заполни форму за 4 шага и стань участником ОЧКИ 2026.
        </p>
      </div>

      {/* Stepper */}
      <div className="mb-12">
        <div className="flex justify-between relative">
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -translate-y-1/2 -z-10" />
          <div
            className="absolute top-1/2 left-0 h-1 bg-primary-500 -translate-y-1/2 -z-10 transition-all duration-300"
            style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
          />
          {steps.map((step) => (
            <div key={step.id} className="flex flex-col items-center">
              <div
                className={`h-12 w-12 rounded-full flex items-center justify-center border-4 ${
                  currentStep >= step.id
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {currentStep > step.id ? <Check className="h-6 w-6" /> : step.id}
              </div>
              <div className="mt-4 text-center">
                <p className="font-bold text-gray-900">{step.title}</p>
                <p className="text-sm text-gray-500">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-8">{renderStep()}</div>

        {submitError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-red-700 text-sm">{submitError}</p>
          </div>
        )}

        <div className="flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Назад
          </button>
          {currentStep < steps.length ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn-primary flex items-center"
              disabled={
                (currentStep === 1 && !selectedCompetence) ||
                (currentStep === 2 && !formData.fullName)
              }
            >
              Далее
              <ChevronRight className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              type="submit"
              className="btn-primary flex items-center"
              disabled={!formData.agreeTerms || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Отправка...
                </>
              ) : (
                <>
                  Отправить заявку
                  <Check className="h-5 w-5 ml-2" />
                </>
              )}
            </button>
          )}
        </div>
      </form>

      <div className="mt-12 text-center text-gray-500 text-sm">
        <p>
          Возникли проблемы с регистрацией?{' '}
          <a href="/contacts" className="text-primary-600 hover:underline">
            Свяжись с поддержкой
          </a>
          .
        </p>
      </div>
      </>
      )}
    </div>
  );
}